import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminStatsPayload, StatsRange } from "./apiTypes";
import { buildFieldMap, formatFieldValue } from "./formatAnswers";
import { MiniBarTable } from "./ui";

export default function AdminAnalysis() {
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get("survey_id") ?? "";
  const [range, setRange] = useState<StatsRange>("1m");
  const [stats, setStats] = useState<AdminStatsPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const fm = useMemo(() => buildFieldMap(), []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const q = new URLSearchParams({ range });
      if (surveyId) q.set("survey_id", surveyId);
      const res = await fetch(`/api/admin/stats?${q.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        setErr("No se pudieron cargar datos.");
        return;
      }
      setStats((await res.json()) as AdminStatsPayload);
    } catch {
      setErr("Error de red.");
    }
  }, [range, surveyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!stats) {
    return <p className="text-sm text-pp-muted">{err ?? "Cargando…"}</p>;
  }

  const { matrix, analysis, drivers } = stats;
  const q = matrix.quadrant_counts;

  const q6labeled = analysis.pain_p2.top_q6.map((x) => ({
    key: formatFieldValue(fm.get("q6_what_breaks"), x.key),
    count: x.count,
  }));

  const q9labeled = analysis.payment.q9_dist.map((x) => ({
    key: formatFieldValue(fm.get("q9_price_sense"), x.key),
    count: x.count,
  }));

  return (
    <div className="space-y-8 text-sm">
      {err && <p className="text-sm text-red-700">{err}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-pp-muted">Ventana:</span>
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as StatsRange)}
          className="border rounded-md p-2"
        >
          <option value="1m">30 días (1m)</option>
          <option value="all">Todo</option>
        </select>
      </div>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">Tamaño del dolor (P2)</p>
        <p>
          Puntaje medio P2: {analysis.pain_p2.avg_score} · % segmento alto (P2):{" "}
          {analysis.pain_p2.pct_alto}% · tiempo semanal aprox. (h):{" "}
          {analysis.pain_p2.avg_time_hours_approx}
        </p>
        <p className="text-pp-muted">Top qué falla (q6)</p>
        <MiniBarTable rows={q6labeled} />
      </section>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">Drivers (combinaciones)</p>
        <ul className="space-y-1 font-mono text-xs">
          {drivers.top_combos.map((x) => (
            <li key={x.label} className="flex justify-between gap-2">
              <span className="break-all flex-1">{x.label}</span>
              <span className="tabular-nums">{x.count}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">
          Matriz 2×2 (score ≥70 vs &lt;70 · penalización ≤-6 vs &gt;-6)
        </p>
        <ul className="space-y-1 text-pp-main">
          <li>
            Q1 Prioridad (alto score, baja penalización): <strong>{q.q1}</strong>
          </li>
          <li>
            Q2 Difícil adopción (alto score, alta penalización): <strong>{q.q2}</strong>
          </li>
          <li>
            Q3 No foco (bajo score, alta penalización): <strong>{q.q3}</strong>
          </li>
          <li>
            Q4 Oportunidad (bajo score, baja penalización): <strong>{q.q4}</strong>
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">Pago proxy</p>
        <p>% con gasto &gt; 0: {analysis.payment.pct_spend_gt0}%</p>
        <p>% q9 umbral &gt;20 o &gt;40: {analysis.payment.pct_q9_gt20}% (gt40 solo: {analysis.payment.pct_q9_gt40}%)</p>
        <p className="text-pp-muted">Distribución q9</p>
        <MiniBarTable rows={q9labeled} />
      </section>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">Canal probable</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            q3 “tercero” en P2: {analysis.canal.pct_q3_third_party_p2}%
            {analysis.canal.prescriptor_relevant
              ? " — prescriptor relevante (≥25%)."
              : " — bajo 25%."}
          </li>
          <li>
            P3 compromiso sí/tal vez: {analysis.canal.pct_p3_commit_yes_maybe}%
            {analysis.canal.professional_strong
              ? " — canal profesional fuerte (≥40%)."
              : " — bajo 40%."}
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <p className="font-medium text-pp-main">Conclusión</p>
        <ul className="list-disc pl-5 space-y-1 text-pp-main">
          {analysis.conclusion.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
